import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronDown, ChevronRight, Star, Calendar, Building2, User, Trash2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_LABELS = { 1: "Poor", 2: "Fair", 3: "Average", 4: "Good", 5: "Excellent" };

function RatingBadge({ rating }) {
  if (!rating) return <span className="text-xs text-slate-400">N/A</span>;
  const colors = {
    1: "bg-red-100 text-red-700",
    2: "bg-orange-100 text-orange-700",
    3: "bg-yellow-100 text-yellow-700",
    4: "bg-green-100 text-green-700",
    5: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${colors[rating] || "bg-slate-100 text-slate-600"}`}>
      {rating}/5 — {RATING_LABELS[rating]}
    </span>
  );
}

export default function FeedbackResponses({ onBack }) {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const fetchResponses = async () => {
      try {
        const res = await axios.get(`${API}/feedback/responses`, { headers: getAuthHeaders() });
        setResponses(res.data || []);
      } catch (err) {
        console.error("Failed to fetch feedback:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResponses();
  }, []);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const getOverallAvg = (sections) => {
    let total = 0, count = 0;
    for (const s of sections || []) {
      for (const r of s.ratings || []) {
        if (r.rating) { total += r.rating; count++; }
      }
    }
    return count > 0 ? (total / count).toFixed(1) : "N/A";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this feedback response?")) return;
    try {
      await axios.delete(`${API}/feedback/responses/${id}`, { headers: getAuthHeaders() });
      setResponses((prev) => prev.filter((fb) => fb.id !== id));
    } catch (err) {
      console.error("Failed to delete feedback:", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="feedback-responses-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="feedback-responses-title">Feedback Responses</h1>
          <p className="text-sm text-slate-500 mt-1">Customer satisfaction questionnaire submissions</p>
        </div>

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : responses.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <Star className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No feedback received yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {responses.map((fb) => {
              const isOpen = expanded[fb.id];
              const avg = getOverallAvg(fb.sections);
              return (
                <div key={fb.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`feedback-card-${fb.id}`}>
                  {/* Summary Row */}
                  <button
                    onClick={() => toggle(fb.id)}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="flex-shrink-0">
                        {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Building2 className="h-4 w-4 text-slate-400" />
                          <span className="font-semibold text-slate-900 truncate">{fb.company_name}</span>
                        </div>
                        {fb.contact_name && (
                          <div className="flex items-center gap-2 mt-0.5">
                            <User className="h-3 w-3 text-slate-300" />
                            <span className="text-xs text-slate-500">{fb.contact_name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className={`text-sm font-bold ${parseFloat(avg) >= 4 ? "text-green-600" : parseFloat(avg) >= 3 ? "text-yellow-600" : "text-red-600"}`}>
                        Avg: {avg}/5
                      </span>
                      <div className="flex items-center gap-1 text-xs text-slate-400">
                        <Calendar className="h-3 w-3" />
                        {fb.submitted_at ? new Date(fb.submitted_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                      </div>
                    </div>
                  </button>

                  {/* Expanded Detail */}
                  {isOpen && (
                    <div className="px-5 pb-5 border-t border-slate-100">
                      {(fb.sections || []).map((section, si) => (
                        <div key={si} className="mt-4">
                          <h3 className="text-sm font-bold text-slate-700 mb-2">{section.title}</h3>
                          <div className="space-y-1.5">
                            {(section.ratings || []).map((r, ri) => (
                              <div key={ri} className="flex items-center justify-between text-sm">
                                <span className="text-slate-600">{r.question}</span>
                                <RatingBadge rating={r.rating} />
                              </div>
                            ))}
                          </div>
                          {section.comments && (
                            <p className="text-xs text-slate-500 mt-2 italic bg-slate-50 rounded px-3 py-2">
                              {section.comments}
                            </p>
                          )}
                        </div>
                      ))}

                      {/* Additional Feedback */}
                      <div className="mt-5 pt-4 border-t border-slate-100">
                        <h3 className="text-sm font-bold text-slate-700 mb-2">Additional Feedback</h3>
                        {fb.improvements && (
                          <div className="mb-2">
                            <span className="text-xs font-semibold text-slate-500">Improvements:</span>
                            <p className="text-sm text-slate-700">{fb.improvements}</p>
                          </div>
                        )}
                        <div className="flex gap-6 text-sm">
                          <span className="text-slate-600">
                            Follow-up meeting: <strong>{fb.follow_up_meeting === true ? "Yes" : fb.follow_up_meeting === false ? "No" : "—"}</strong>
                          </span>
                          <span className="text-slate-600">
                            Regular catch-ups: <strong>{fb.regular_catchups === true ? "Yes" : fb.regular_catchups === false ? "No" : "—"}</strong>
                          </span>
                        </div>
                        {fb.additional_comments && (
                          <p className="text-xs text-slate-500 mt-2 italic bg-slate-50 rounded px-3 py-2">
                            {fb.additional_comments}
                          </p>
                        )}
                      </div>

                      {/* Delete */}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 border-red-200"
                          onClick={() => handleDelete(fb.id)}
                          data-testid={`delete-feedback-${fb.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
